/**
 * `/i/<code>` — an invite a member shared (GH-864e, plan §1.2 "Entry path").
 *
 * The universal link opens the app where it is installed; this page is what everyone else sees.
 * It offers the two ways in, and the code itself so a donor who installs the app first can paste
 * it there ("Have an invite?") — deferred deep linking across an install is unreliable without a
 * third party, and this product adds none.
 *
 * ## One "unavailable", whatever the reason
 *
 * Unknown, used, revoked and expired all read the same here, as they do on the server
 * (`invite_unavailable`, and `publicInviteResponse`'s single shape), so the page cannot be used to
 * learn which codes exist or what became of somebody's invite. A malformed code never reaches the
 * server and reads the same way too. With `FEATURE_MEMBERSHIP` off the routes answer 404 and the
 * page says the same sentence, without the waitlist.
 *
 * ## What it never does
 *
 * Gate a gift. Nothing here sits on the give path, and the page says so: giving never needs an
 * invite (plan §1.1).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  inviteCodeSchema,
  MEMBERSHIP_COPY,
  type DonorMembershipResponse,
  type DonorSessionResponse,
  type PublicInviteResponse,
} from "@givehere/contracts";
import { fetchPublicInvite } from "./api.js";
import { fetchDonorMembership, redeemInvite, storedToken } from "./donor-session.js";
import { ALREADY_MEMBER, redeemFailure } from "./InviteCodeForm.js";
import { useMembershipPulse } from "./membership-hooks.js";
import { grouped } from "./share.js";
import { SignInSheet } from "./SignInSheet.js";

/** The App Store listing, a build argument like the other public names (`legal/site.ts`). */
export const APP_STORE_URL: string | null = import.meta.env["VITE_APP_STORE_URL"] || null;

type View =
  | { kind: "loading" }
  | { kind: "available"; invite: PublicInviteResponse }
  /** `gateOpen` decides whether the waitlist is offered as the way forward. */
  | { kind: "unavailable"; gateOpen: boolean }
  | { kind: "joined"; membership: DonorMembershipResponse }
  | { kind: "already" };

/** "ABCDE-FGHJK": the code in two halves, the way a person reads one aloud. */
const spaced = (code: string): string => `${code.slice(0, 5)}-${code.slice(5)}`;

export function InvitePage({
  code: rawCode,
  appStoreUrl = APP_STORE_URL,
}: {
  code: string;
  appStoreUrl?: string | null;
}) {
  const parsed = inviteCodeSchema.safeParse(rawCode);
  const code = parsed.success ? parsed.data : null;
  const [view, setView] = useState<View>(
    code ? { kind: "loading" } : { kind: "unavailable", gateOpen: false },
  );
  const [signingIn, setSigningIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // A malformed code is never sent, so the gate is read from the pulse instead — and the page then
  // looks exactly like an unknown code's.
  const pulse = useMembershipPulse(code === null);

  useEffect(() => {
    if (!code) return;
    let live = true;
    fetchPublicInvite(code).then(
      (invite) => {
        if (!live) return;
        if (invite === null) setView({ kind: "unavailable", gateOpen: false });
        else if (invite.status === "available") setView({ kind: "available", invite });
        else setView({ kind: "unavailable", gateOpen: true });
      },
      () => live && setView({ kind: "unavailable", gateOpen: false }),
    );
    return () => {
      live = false;
    };
  }, [code]);

  const redeem = async (token: string) => {
    if (!code) return;
    setError(null);
    setBusy(true);
    try {
      setView({ kind: "joined", membership: await redeemInvite(token, code) });
    } catch (caught: unknown) {
      const message = redeemFailure(caught);
      if (message === MEMBERSHIP_COPY.inviteUnavailable) {
        setView({ kind: "unavailable", gateOpen: true });
      } else if (message === ALREADY_MEMBER) {
        setView({ kind: "already" });
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  /*
    GH-871b: a new person's sign-in carried this code (`inviteCode`) and the server redeemed it
    with the account — `membershipJoined` says so — so there is nothing left to redeem; read the
    membership to welcome them. An existing account signed in without it, and redeems as before.
  */
  const afterSignIn = async (issued: DonorSessionResponse) => {
    if (!issued.membershipJoined) return redeem(issued.token);
    const membership = await fetchDonorMembership(issued.token).catch(() => null);
    setView(membership ? { kind: "joined", membership } : { kind: "already" });
  };

  const join = () => {
    const token = storedToken();
    if (token) void redeem(token);
    else setSigningIn(true);
  };

  if (view.kind === "loading") {
    return (
      <main className="card card--landing membership-page">
        <p className="notice">Loading…</p>
      </main>
    );
  }

  if (view.kind === "unavailable") {
    return (
      <main className="card card--landing membership-page">
        <h1 className="landing__event">{MEMBERSHIP_COPY.inviteUnavailable}</h1>
        <p className="landing__about">{MEMBERSHIP_COPY.inviteUnavailableBody}</p>
        {(view.gateOpen || (pulse !== undefined && pulse !== null)) && (
          <a className="pay landing__give" href="/waitlist">
            {MEMBERSHIP_COPY.joinWaitlist}
          </a>
        )}
        <a className="link landing__quiet" href="/">
          Find an organization
        </a>
      </main>
    );
  }

  if (view.kind === "joined" || view.kind === "already") {
    const member = view.kind === "joined" ? view.membership.member : null;
    return (
      <main className="card card--landing membership-page">
        <FocusedHeading>
          {member ? MEMBERSHIP_COPY.welcomeMember(grouped(member.number)) : ALREADY_MEMBER}
        </FocusedHeading>
        {member?.founding && (
          <p className="membership-page__keyline">{MEMBERSHIP_COPY.foundingMember}</p>
        )}
        {appStoreUrl && (
          <a className="pay landing__give" href={appStoreUrl}>
            Get the app
          </a>
        )}
        <a className="link landing__quiet" href="/">
          Find an organization
        </a>
      </main>
    );
  }

  const { invite } = view;
  return (
    <main className="card card--landing membership-page">
      <h1 className="landing__event" dir="auto">
        {invite.inviterName
          ? MEMBERSHIP_COPY.invitedYou(invite.inviterName)
          : MEMBERSHIP_COPY.invitedYouAnonymous}
      </h1>
      <p className="landing__about">Join GiveHere with this invite. Giving never needs one.</p>

      {appStoreUrl && (
        <a className="pay landing__give" href={appStoreUrl}>
          Get the app
        </a>
      )}
      <button
        type="button"
        className={appStoreUrl ? "pay pay--outline" : "pay landing__give"}
        disabled={busy}
        onClick={join}
      >
        {busy ? "Joining…" : "Join on the web"}
      </button>
      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}

      {code && (
        <section className="invite-code" aria-labelledby="invite-code-title">
          <h2 id="invite-code-title" className="invite-code__label">
            Your invite code
          </h2>
          <p className="invite-code__value" translate="no">
            {spaced(code)}
          </p>
          <button
            type="button"
            className="link"
            onClick={() =>
              void navigator.clipboard
                ?.writeText(code)
                .then(() => setCopied(true))
                .catch(() => setCopied(false))
            }
          >
            Copy
          </button>
          <span className="muted small" role="status">
            {copied ? "Copied" : ""}
          </span>
          <p className="muted small">
            In the app, tap “{MEMBERSHIP_COPY.haveInvite}” and paste it.
            {invite.expiresOn ? ` Works until ${readableDate(invite.expiresOn)}.` : ""}
          </p>
        </section>
      )}

      {signingIn && (
        <SignInSheet
          heading="Sign in to join"
          lede="Your invite is used once you’re signed in."
          inviteCode={code}
          onIssued={(issued) => {
            setSigningIn(false);
            return afterSignIn(issued);
          }}
          onDismiss={() => setSigningIn(false)}
        />
      )}
    </main>
  );
}

/**
 * The outcome's heading, focused when it appears: the whole card was replaced, so a screen reader
 * is moved to the new first line the way a navigation would move it.
 */
export function FocusedHeading({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => ref.current?.focus(), []);
  return (
    <h1 className="landing__event" ref={ref} tabIndex={-1}>
      {children}
    </h1>
  );
}

/** "October 30" from "2026-10-30", read as a calendar date rather than a UTC instant. */
function readableDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });
}
